import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Printer, X, Loader2 } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { api } from '@/lib/api'

interface OPCard {
  op_id: number
  op_numero: number
  cliente_nome: string | null
  produto_codigo: string | null
  produto_descricao: string
  variante: string
  quantidade: string
  peso_planejado: string
  fim_previsto: string | null
  maquina_codigo: string | null
  pedido_numero: number | null
  observacao: string | null
  qr_token: string
  etapas: Array<{ codigo: string; nome: string }>
}

export default function ImprimirOPsPage() {
  const [params] = useSearchParams()
  const ids = params.get('ids')?.split(',').map(Number).filter(Boolean) || []
  const [cards, setCards] = useState<OPCard[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { load() }, [])

  async function load() {
    if (ids.length === 0) {
      setLoading(false)
      return
    }
    try {
      const out: OPCard[] = []
      for (const id of ids) {
        const [op, qr] = await Promise.all([
          api.get(`/industrial/ordens/${id}`).then(r => r.data),
          api.post(`/industrial/ordens/${id}/qr-token`).then(r => r.data),
        ])
        out.push({
          op_id: op.id,
          op_numero: op.numero,
          cliente_nome: op.cliente_nome || null,
          produto_codigo: op.produto_codigo,
          produto_descricao: op.produto_descricao || '',
          variante: op.variante || 'PADRAO',
          quantidade: op.quantidade,
          peso_planejado: op.peso_planejado || '0',
          fim_previsto: op.fim_previsto,
          maquina_codigo: op.maquina_codigo,
          pedido_numero: op.pedido_numero || null,
          observacao: op.observacao,
          qr_token: qr.token,
          etapas: op.etapas?.map((e: any) => ({
            codigo: e.etapa_codigo,
            nome: e.etapa_nome,
          })) || [],
        })
      }
      setCards(out)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const baseUrl = window.location.origin

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-12 w-12 animate-spin text-violet-600" />
    </div>
  }

  if (cards.length === 0) {
    return <div className="min-h-screen flex items-center justify-center text-ink-500">
      Nenhuma OP selecionada. Use ?ids=1,2,3 na URL.
    </div>
  }

  return (
    <div className="bg-white">
      <style>{`
        @page { size: A4; margin: 10mm; }
        @media print {
          .no-print { display: none !important; }
          .op-card { page-break-inside: avoid; page-break-after: always; }
          .op-card:last-child { page-break-after: auto; }
        }
        .op-card { border: 2px solid #000; margin-bottom: 12mm; }
      `}</style>

      <div className="no-print bg-ink-100 py-4 sticky top-0 z-10 shadow flex justify-center gap-3 print:hidden">
        <button
          onClick={() => window.print()}
          className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg flex items-center gap-2"
        >
          <Printer className="h-5 w-5" /> Imprimir {cards.length} OP(s)
        </button>
        <button
          onClick={() => window.close()}
          className="px-6 py-3 bg-ink-300 hover:bg-ink-400 text-ink-900 rounded-lg flex items-center gap-2"
        >
          <X className="h-5 w-5" /> Fechar
        </button>
      </div>

      <div className="max-w-[210mm] mx-auto p-4 print:p-0">
        {cards.map((c) => {
          const mobileUrl = `${baseUrl}/m/op/${c.qr_token}`
          return (
            <div key={c.op_id} className="op-card bg-white">
              {/* Cabeçalho com QR */}
              <div className="flex border-b-2 border-black">
                <div className="w-1/4 p-3 border-r border-black flex items-center justify-center">
                  <h1 className="text-2xl font-extrabold text-ink-900">
                    VELORE ERP<span className="text-violet-600">ERP</span>
                  </h1>
                </div>
                <div className="flex-1 p-3 text-center flex flex-col justify-center">
                  <h2 className="text-lg font-bold">ORDEM DE PRODUÇÃO</h2>
                  <div className="text-xs text-ink-500">Controle de Produção · Industrial</div>
                </div>
                <div className="w-1/5 p-2 border-l border-black text-center">
                  <QRCodeSVG value={mobileUrl} size={100} level="M" />
                  <div className="text-[8px] text-ink-500 mt-1">📱 Escaneie pra apontar</div>
                </div>
              </div>

              {/* Número GIGANTE */}
              <div className="bg-black text-white p-3 text-center">
                <div className="text-3xl font-extrabold tracking-widest">
                  OP #{c.op_numero}
                </div>
                {c.pedido_numero && (
                  <div className="text-xs mt-1">Pedido #{c.pedido_numero}</div>
                )}
              </div>

              {/* Cliente */}
              <div className="flex border-b border-black">
                <div className="flex-[2] p-2 border-r border-black">
                  <div className="text-[9px] uppercase text-ink-500">Cliente</div>
                  <div className="font-bold text-sm">{c.cliente_nome || '—'}</div>
                </div>
                <div className="flex-1 p-2 border-r border-black">
                  <div className="text-[9px] uppercase text-ink-500">Variante</div>
                  <div className="font-bold text-sm">{c.variante}</div>
                </div>
                <div className="flex-1 p-2">
                  <div className="text-[9px] uppercase text-ink-500">Entrega prev.</div>
                  <div className={`font-bold text-sm ${c.fim_previsto && new Date(c.fim_previsto) < new Date() ? 'text-red-700' : ''}`}>
                    {c.fim_previsto ? new Date(c.fim_previsto).toLocaleDateString('pt-BR') : '—'}
                  </div>
                </div>
              </div>

              {/* Produto */}
              <div className="p-3 border-b border-black bg-ink-50">
                <div className="font-bold text-base">{c.produto_descricao}</div>
                <div className="text-xs text-ink-500 font-mono mt-0.5">
                  {c.produto_codigo || ''}
                </div>
              </div>

              {/* Grid 4 colunas */}
              <div className="grid grid-cols-4 border-b border-black">
                <div className="p-2 border-r border-black text-center">
                  <div className="text-[9px] uppercase text-ink-500">Quantidade</div>
                  <div className="text-xl font-extrabold">{Number(c.quantidade).toLocaleString('pt-BR')}</div>
                </div>
                <div className="p-2 border-r border-black text-center">
                  <div className="text-[9px] uppercase text-ink-500">Peso (kg)</div>
                  <div className="text-xl font-extrabold">{Number(c.peso_planejado).toLocaleString('pt-BR')}</div>
                </div>
                <div className="p-2 border-r border-black text-center">
                  <div className="text-[9px] uppercase text-ink-500">Máquina</div>
                  <div className="text-base font-bold">{c.maquina_codigo || '—'}</div>
                </div>
                <div className="p-2 text-center">
                  <div className="text-[9px] uppercase text-ink-500">Etapas</div>
                  <div className="text-xl font-extrabold">{c.etapas.length}</div>
                </div>
              </div>

              {/* Etapas com checkboxes */}
              <div className="p-3 border-b border-black">
                <div className="text-[10px] uppercase text-ink-500 mb-2">Acompanhamento de etapas</div>
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(c.etapas.length || 1, 5)}, 1fr)` }}>
                  {c.etapas.map((e, i) => (
                    <div key={i} className="border border-ink-300 rounded p-2 text-center">
                      <div className="text-[10px] font-bold">{e.nome}</div>
                      <div className="w-5 h-5 border-2 border-black mx-auto my-2 rounded-sm"></div>
                      <div className="text-[10px]">____/____</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Observações */}
              <div className="p-3 border-b border-black min-h-[40px]">
                <div className="text-[10px] uppercase text-ink-500 mb-1">Observações</div>
                <div className="text-xs">{c.observacao || ''}</div>
              </div>

              {/* Rodapé com assinaturas */}
              <div className="grid grid-cols-3 bg-ink-100 text-[9px]">
                <div className="p-2 border-r border-ink-300 text-center">
                  <strong>Operador:</strong>
                  <div className="border-b border-ink-400 mt-3 mb-1 mx-2"></div>
                </div>
                <div className="p-2 border-r border-ink-300 text-center">
                  <strong>Conferência:</strong>
                  <div className="border-b border-ink-400 mt-3 mb-1 mx-2"></div>
                </div>
                <div className="p-2 text-center">
                  <strong>Expedição:</strong>
                  <div className="border-b border-ink-400 mt-3 mb-1 mx-2"></div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
